import type { ErrorEvent } from '@sentry/react';

// Injected login/autofill bridges (Facebook / Instagram in-app WebViews) wrap
// our page with a MutationObserver that reports login fields to a native bridge
// via postMessage. When the bridge rejects the payload it throws from code that
// is not part of our bundle. None of these function names exist in our source
// (Sentry WEB-CLIENT-258 / WEB-CLIENT-1ET).
const injectedBridgeFunctions = new Set([
  'dispatchToBridge',
  'sendPostMessage',
  'logLoginFieldDetected',
  'mutationObserverCallback',
  'throttledMutationObserverCallback',
]);

// Globals that only an in-app browser's own injected script defines. When that
// script runs before (or instead of) the native object it expects, it throws a
// bare ReferenceError for the missing global. The script is evaluated in the
// document's scope, so Sentry attributes the frame to the page URL — on our
// hostname, past the external-URL check below — and the message names the only
// thing that identifies it. None of these names exist anywhere in our source.
//  - SCDynimacBridge: Snapchat's iOS in-app browser (Sentry WEB-CLIENT-27D)
//  - zaloJSV2: Zalo's in-app browser (also Sentry WEB-CLIENT-27D — Sentry
//    grouped this second injected-global shape into the same issue as the
//    Snapchat one above)
const injectedBridgeGlobals = ['SCDynimacBridge', 'zaloJSV2'];

/**
 * Decide whether a Sentry error event should be reported. Returns the event to
 * keep it, or `null` to drop it. Extracted from `Sentry.init`'s `beforeSend` so
 * the (growing) set of noise filters can be unit tested in isolation.
 *
 * Everything dropped here originates outside our codebase — unsupported/in-app
 * browsers, browser extensions, injected native bridges, and DOM-mutating
 * translation tools. We only care about exceptions caused by our own code.
 */
export const filterSentryEvent = (event: ErrorEvent): ErrorEvent | null => {
  const exception = event.exception?.values?.[0];
  if (!exception) {
    return event;
  }

  const stacktrace = exception.stacktrace;
  const frames = stacktrace?.frames ?? [];
  const value = exception.value ?? '';

  // A frame that lives on our running origin and isn't the bundled vendor chunk
  // is our own application code. Filters that suppress noise thrown from within a
  // dependency use this to make sure a genuine first-party defect sharing the
  // same message/type is never dropped.
  const hasFirstPartyAppFrame = frames.some((frame) => {
    const filename = frame.filename || '';
    return (
      filename.includes(window.location.hostname) &&
      !filename.includes('/vendor.')
    );
  });

  // Drop JS module parse failures on browsers too old to parse our bundle
  // (unsupported/in-app browsers). These surface as a SyntaxError that either
  // has no stacktrace at all, or whose frames are all native `parseModule` code
  // with no first-party file — a reload can never help, it's an unsupported
  // browser, not our code (Sentry WEB-CLIENT-24K / WEB-CLIENT-24Z). Guard
  // against JSON.parse errors, which always mention "JSON" and do originate from
  // our code (those carry a real first-party stacktrace). `[].every()` is true,
  // so the no-stacktrace variant is covered.
  const isModuleParseError =
    exception.type === 'SyntaxError' &&
    !value.includes('JSON') &&
    frames.every(
      (frame) => !frame.filename || frame.filename === '[native code]'
    );

  if (isModuleParseError) {
    return null;
  }

  // A dynamically imported chunk that downloaded but won't parse surfaces as a
  // SyntaxError thrown from inside Vite's module preloader (the `preload-helper`
  // chunk) while it evaluates the fetched module. Unlike the native-frame
  // variant above, this carries first-party frames (the preloader plus the
  // `lazy(() => import(...))` site), so the all-native check can't catch it. It
  // only ever means the browser is too old to parse our modern bundle
  // (HeadlessChrome 91 and other unsupported/bot browsers) — a reload can't
  // help and our source is fine (Sentry WEB-CLIENT-24Z). The `!JSON` guard
  // still protects genuine JSON.parse SyntaxErrors from our own code.
  const isChunkParseError =
    exception.type === 'SyntaxError' &&
    !value.includes('JSON') &&
    frames.some((frame) => frame.filename?.includes('preload-helper'));

  if (isChunkParseError) {
    return null;
  }

  // An injected content script (browser extension, in-app browser, bookmarklet)
  // that gets evaluated twice in the same page throws a top-level redeclaration
  // SyntaxError: "Identifier 'x' has already been declared". This can never come
  // from our own code — our bundle is ES modules, where every top-level binding
  // is module-scoped, and a genuine duplicate declaration would fail the build
  // rather than throw at runtime. The injected script runs in the document's own
  // scope, so Sentry attributes the frame to the HTML document URL, which is on
  // our hostname and therefore slips past the external-URL check below (Sentry
  // WEB-CLIENT-27S).
  const isInjectedRedeclaration =
    exception.type === 'SyntaxError' &&
    /^Identifier '[^']*' has already been declared$/.test(value.trim());

  if (isInjectedRedeclaration) {
    return null;
  }

  const isInjectedBridgeError = frames.some(
    (frame) => frame.function && injectedBridgeFunctions.has(frame.function)
  );

  if (isInjectedBridgeError) {
    return null;
  }

  // Both message shapes for an undefined global: "Can't find variable: X"
  // (Safari/WebKit, which every iOS in-app browser is) and "X is not defined"
  // (Chromium/Gecko). Anchored to the global's name so an unrelated
  // ReferenceError that merely mentions it can't slip through.
  const isInjectedBridgeGlobal =
    exception.type === 'ReferenceError' &&
    injectedBridgeGlobals.some((name) =>
      new RegExp(
        `^(Can't find variable: ${name}|${name} is not defined)$`
      ).test(value.trim())
    );

  if (isInjectedBridgeGlobal) {
    return null;
  }

  // Browser extensions and injected analytics scripts walk the DOM and
  // JSON.stringify nodes that carry React's internal fiber back-reference
  // (`__reactFiber$…`), which is circular. Our own code never serializes DOM
  // nodes, so a "Converting circular structure to JSON" error that names a React
  // fiber originates entirely outside our codebase — the injected frames are
  // anonymous/eval'd, so the external-URL check below can't catch them (Sentry
  // WEB-CLIENT-263 / WEB-CLIENT-25N).
  const isDomFiberSerialization =
    value.includes('Converting circular structure to JSON') &&
    value.includes('__reactFiber');

  if (isDomFiberSerialization) {
    return null;
  }

  // Browser translation tools (Safari's built-in translate, Google Translate,
  // etc.) swap out DOM nodes underneath React. When React later commits, it runs
  // removeChild/insertBefore against a node the tool already moved or detached
  // and throws a NotFoundError from entirely within its minified reconciler with
  // no first-party frame, so the external-URL check below can't catch it. Three
  // shapes seen in production, all from React's own DOM writes (our code never
  // calls removeChild/insertBefore directly):
  //  - removeChild:  "…The node to be removed is not a child of this node."
  //                  (WEB-CLIENT-25S)
  //  - insertBefore: "…The node before which the new node is to be inserted is
  //                  not a child of this node." (Chrome DOMException code 8,
  //                  WEB-CLIENT-24S)
  //  - insertBefore: "NotFoundError: The object can not be found here."
  //                  (Safari DOMException code 8, WEB-CLIENT-26Q)
  // The removeChild/insertBefore messages name the failing DOM write, so they're
  // self-identifying. "The object can not be found here" is a generic Safari
  // NotFoundError, so for that shape we also require the stack to name
  // insertBefore to avoid suppressing unrelated application errors that happen to
  // share the message.
  const hasInsertBeforeFrame = frames.some((frame) =>
    frame.function?.includes('insertBefore')
  );
  const isExternalDomMutation =
    exception.type === 'NotFoundError' &&
    (((value.includes('removeChild') || value.includes('insertBefore')) &&
      value.includes('not a child of this node')) ||
      (value.includes('The object can not be found here') &&
        hasInsertBeforeFrame));

  if (isExternalDomMutation) {
    return null;
  }

  // Browsers throw a SecurityError ("The operation is insecure.") when script
  // touches a storage API (localStorage/sessionStorage/cookies/IndexedDB) in a
  // locked-down context — Safari with cross-site tracking prevention, private
  // browsing, or storage disabled. In production this surfaces from the ConfigCat
  // SDK's default LocalStorageConfigCache reading `localStorage` during init,
  // entirely within vendor code that we can't change — it's an unsupported
  // browser configuration, not a defect in our code. The minified frames live in
  // the same-origin vendor chunk, so the exception type + message are the only
  // reliable signal at report time. We still keep any same-message error that
  // carries a first-party app frame, so a genuine storage failure in our own
  // code reports (Sentry WEB-CLIENT-1HW).
  const isBlockedStorageAccess =
    exception.type === 'SecurityError' &&
    value.includes('The operation is insecure');

  if (isBlockedStorageAccess && !hasFirstPartyAppFrame) {
    return null;
  }

  // Amplitude's analytics SDK opens its own IndexedDB for diagnostics and, in
  // its raw `onupgradeneeded` handler, reads `event.target.result.objectStoreNames`.
  // In some in-app WebViews (e.g. the Facebook browser on iOS) that event fires
  // with an undefined database handle, so the SDK throws from entirely within
  // @amplitude/analytics-core: "undefined is not an object (evaluating
  // 'e.objectStoreNames')" (Safari) / "Cannot read properties of undefined
  // (reading 'objectStoreNames')" (Chromium). Amplitude is bundled into the
  // `vendor` chunk (vite.config.mts's `isVendorChunk` — keep `@amplitude`
  // listed there, or this filter silently stops matching), so this noise
  // carries no first-party application frame.
  // Our own IndexedDB access goes through `idb`'s `upgrade(db)` callback —
  // which always receives a defined handle and, when running our code, sits in
  // a first-party app chunk. So we only drop the undefined-access variant when
  // there is no first-party frame to attribute it to, keeping any genuine
  // first-party `objectStoreNames` failure (Sentry WEB-CLIENT-1JZ).
  const isUndefinedObjectStoreNamesAccess =
    exception.type === 'TypeError' &&
    value.includes('objectStoreNames') &&
    (value.includes('undefined is not an object') ||
      value.includes('Cannot read properties of undefined'));

  if (isUndefinedObjectStoreNamesAccess && !hasFirstPartyAppFrame) {
    return null;
  }

  // Amplitude's analytics SDK gzip-compresses each event batch before sending,
  // piping a Blob through the native Compression Streams API entirely within
  // its own transport code: `new Blob([data]).stream().pipeThrough(new
  // CompressionStream('gzip'))`, then `new Response(stream).arrayBuffer()`. A
  // WebKit bug in that native pipeline intermittently throws a bare
  // "NotReadableError: The I/O read operation failed." with no first-party
  // frame (frames are either all-vendor/native, or absent entirely). It
  // recurs across otherwise unrelated pages under a fresh issue each time,
  // since Sentry groups by culprit/page rather than message — a property of
  // the browser's Compression Streams implementation, not of any page content
  // (Sentry WEB-CLIENT-23W / WEB-CLIENT-2AH / WEB-CLIENT-2AW).
  const isAmplitudeCompressionIOError =
    exception.type === 'NotReadableError' &&
    value.includes('The I/O read operation failed');

  if (isAmplitudeCompressionIOError && !hasFirstPartyAppFrame) {
    return null;
  }

  // Some extensions and injected agents harden the JS realm (SES/lockdown-style)
  // by freezing intrinsics before our bundle runs. Any dependency that sets up
  // its own prototypes at import time then fails against the frozen originals
  // with "Cannot assign to read only property '<name>'" — first seen from
  // @amplitude/element-selector assigning `Step.prototype.toString` (Sentry
  // WEB-CLIENT-27K), but the property involved is incidental: it's whichever one
  // the hardened realm happened to freeze. Match the assignment failure itself
  // rather than a specific property. Vendor code is bundled into the same-origin
  // `vendor` chunk, so the external-URL check below can't catch it — but that
  // also means a genuine first-party version of this error would carry an app
  // frame, so requiring no first-party frame keeps one reportable.
  const isFrozenPrototypeAssignment =
    exception.type === 'TypeError' &&
    value.includes("Cannot assign to read only property '");

  if (isFrozenPrototypeAssignment && !hasFirstPartyAppFrame) {
    return null;
  }

  // TipTap/ProseMirror throws a TransformError from prosemirror-transform's
  // split/join step when splitBlock runs at certain list-boundary cursor
  // positions — reachable simply by pressing Enter. It originates entirely
  // within @tiptap/core + prosemirror-* (our code never calls tr.split), is a
  // known upstream edge case, and the editor recovers: the failing transaction
  // is never dispatched, so the document is unchanged and the next keystroke
  // works. The same underlying split/join bug surfaces under at least two
  // message shapes — "Inserted content deeper than insertion position" and
  // "Cannot join <nodeType> onto <nodeType>" (e.g. "Cannot join bulletList onto
  // paragraph") — so both are matched here rather than just the first one seen.
  // The minified stack sits in the same-origin vendor chunk, so only the
  // message identifies it — the same class as the prosemirror race still
  // dropped via ignoreErrors (Selection.collapse) (Sentry WEB-CLIENT-1DZ).
  const isProseMirrorSplitError =
    value.includes('Inserted content deeper than insertion position') ||
    /^Cannot join \w+ onto \w+$/.test(value);

  if (isProseMirrorSplitError) {
    return null;
  }

  // Pressing Enter at certain cursor positions makes TipTap's splitBlock call
  // tr.setNodeMarkup with the default block type, and prosemirror-transform
  // throws "Invalid content for node type paragraph" when the node's current
  // content isn't valid for it. The whole stack is @tiptap/core + prosemirror-*
  // in the vendor chunk, the transaction is never dispatched, and the next
  // keystroke works, so it is the same class as the split errors above. Requiring
  // a vendor-chunk frame and no first-party frame keeps a genuine schema
  // violation in our own commands, or one thrown from another script, reportable
  // (Sentry WEB-CLIENT-2CK).
  const isProseMirrorInvalidContent =
    exception.type === 'RangeError' &&
    /^Invalid content for node type \w+$/.test(value.trim()) &&
    frames.some((frame) => (frame.filename || '').includes('/vendor.'));

  if (isProseMirrorInvalidContent && !hasFirstPartyAppFrame) {
    return null;
  }

  // Safari-based in-app browsers (Apple Mail) reject IndexedDB opens with
  // "Database deleted by request of the user" when the viewer clears site data
  // or the app tears the web view down. It is a storage lifecycle event outside
  // our control and arrives with no stacktrace, so only the message identifies
  // it. An empty frame list is required so a first-party storage call that
  // rejects with the same message, and so carries a stack, stays reportable
  // (Sentry WEB-CLIENT-2CM).
  const isIndexedDbDeletedByUser =
    value.includes('Database deleted by request of the user') &&
    frames.length === 0;

  if (isIndexedDbDeletedByUser) {
    return null;
  }

  // pdfjs-dist ships its worker as an ES module that references `import.meta`.
  // Browsers without module-worker support (e.g. Firefox < 114) can't parse the
  // bundled pdf.worker chunk and throw "SyntaxError: import.meta may only appear
  // in a module" before pdfjs can run. It's an unsupported-browser parse failure
  // — the same class as the module-parse errors above — and is unreachable in
  // any browser that can actually run the worker. `import.meta` outside a module
  // is only ever this, so the message alone identifies it (Sentry
  // WEB-CLIENT-23S).
  const isWorkerModuleSyntaxError = value.includes(
    'import.meta may only appear in a module'
  );

  if (isWorkerModuleSyntaxError) {
    return null;
  }

  // The global unhandledrejection handler sometimes captures a "rejection" that
  // carries no Error and no stacktrace, so it can't be tied to any of our code:
  //  - An injected script/extension dispatches a *synthetic* `unhandledrejection`
  //    CustomEvent on window (isTrusted: false). Sentry serializes it as "Event
  //    `CustomEvent` (type=unhandledrejection) captured as promise rejection"
  //    (Sentry WEB-CLIENT-YV).
  //  - A bare empty object is rejected, serializing to "…captured as promise
  //    rejection with keys: [object has no keys]" (Sentry WEB-CLIENT-1FB).
  // Our own code always rejects with an Error, which carries a first-party
  // stack, so requiring an empty frame list keeps any genuine rejection
  // reportable while dropping these unattributable ones.
  const isUnattributablePromiseRejection =
    frames.length === 0 &&
    value.includes('captured as promise rejection') &&
    (value.includes('[object has no keys]') ||
      value.includes('type=unhandledrejection'));

  if (isUnattributablePromiseRejection) {
    return null;
  }

  // An injected automation/accessibility bridge rejects with a bare string of
  // the shape "Object Not Found Matching Id:4, MethodName:update, ParamCount:4".
  // It's the wire format of a host↔page RPC shim (seen from Office/Outlook's
  // link handling and similar embedded viewers) complaining that the object id
  // it was handed no longer resolves. It arrives through the global
  // unhandledrejection handler with no stacktrace and no Error, so no frame can
  // attribute it. None of "MethodName", "ParamCount" or that phrasing exists
  // anywhere in our source, and our own code always rejects with an Error that
  // carries a first-party stack — so the message shape plus an empty frame list
  // drops it without risking a genuine rejection (Sentry WEB-CLIENT-96).
  const isInjectedRpcObjectMiss =
    frames.length === 0 &&
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/.test(
      value
    );

  if (isInjectedRpcObjectMiss) {
    return null;
  }

  // In-app browsers (DuckDuckGo Mobile, and other embedded WebViews) reject a
  // bare `Error: invalid origin` from their own injected navigation/postMessage
  // bridge. It surfaces through the global unhandledrejection handler with no
  // stacktrace at all, so it can't be tied to any frame. The string "invalid
  // origin" exists nowhere in our source, and our own code always rejects with
  // an Error that carries a first-party stack — so an exact-message match with
  // an empty frame list drops this injected noise without risking a genuine
  // first-party rejection (Sentry WEB-CLIENT-173).
  const isInjectedInvalidOrigin =
    frames.length === 0 && value.trim().toLowerCase() === 'invalid origin';

  if (isInjectedInvalidOrigin) {
    return null;
  }

  // Only an actual stack of external frames is noise. An empty frame list must
  // not qualify — `[].every()` is true, which would silently drop any event
  // that reached here without frames.
  if (frames.length > 0) {
    const isExternalError = frames.every((frame) => {
      // Check if the frame is from an external source
      const filename = frame.filename || '';
      return (
        // Filter out browser extensions
        filename.startsWith('chrome-extension://') ||
        filename.startsWith('moz-extension://') ||
        // Filter out other external scripts
        (filename.includes('://') &&
          !filename.includes(window.location.hostname)) ||
        // Filter out eval/anonymous scripts
        filename === '<anonymous>' ||
        filename.includes('eval')
      );
    });

    if (isExternalError) {
      return null;
    }
  }

  return event;
};
